import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../context/AuthContext';
import {
  addAdminToClassroom,
  addStudentToClassroom,
  ClassroomInSchool,
  createClassroom,
  deleteClassroom,
  deleteStudentFromClassroom,
  fetchClassesInMySchool,
  fetchMyEnrollRequests,
  fetchStudentsInClassroom,
  fetchTeachersInClassroom,
  fetchClassroomStats,
  joinClassroom,
  leaveClassroom,
  MyEnrollRequest,
  renameClassroom,
  StudentCreatePayload,
  StudentSummary,
  TeacherInClassroom,
} from '../../lib/classroomsApi';
import { parseApiError } from './utils';
import ClassroomsListView from './components/ClassroomsListView';
import ClassroomDetailView from './components/ClassroomDetailView';

const ClassManagementHub: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me, uiRole, isLoading: authLoading, signOut } = useAuth();
  const isB2B = (me?.school_mode || 'b2c') === 'b2b';

  const classIdParam = searchParams.get('classId');
  const actionParam = searchParams.get('action');
  const tabParam = searchParams.get('tab');
  const selectedClassId = classIdParam ? Number(classIdParam) : null;
  const activeTab = (tabParam === 'teme' ||
  tabParam === 'cursuri' ||
  tabParam === 'students' ||
  tabParam === 'catalog' ||
  tabParam === 'orar' ||
  tabParam === 'profesori'
    ? tabParam
    : 'catalog') as
    | 'catalog'
    | 'teme'
    | 'cursuri'
    | 'students'
    | 'orar'
    | 'profesori';

  const [isSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [classes, setClasses] = useState<ClassroomInSchool[]>([]);
  const [enrollRequests, setEnrollRequests] = useState<MyEnrollRequest[]>([]);

  const selectedClass = useMemo(() => {
    if (!selectedClassId) return null;
    return classes.find((c) => c.id === selectedClassId) || null;
  }, [classes, selectedClassId]);

  const [students, setStudents] = useState<StudentSummary[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState<string | null>(null);

  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameClassId, setRenameClassId] = useState<number | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteClassId, setDeleteClassId] = useState<number | null>(null);

  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [addAdminClassId, setAddAdminClassId] = useState<number | null>(null);
  const [teachers, setTeachers] = useState<TeacherInClassroom[]>([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState<number | null>(null);

  const user = useMemo(() => {
    return {
      role: (uiRole || 'teacher') as any,
      name: me?.full_name || 'Utilizator',
      avatar: me?.avatar_url || undefined,
    };
  }, [me?.avatar_url, me?.full_name, uiRole]);

  const loadBaseData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [cls, req] = await Promise.all([fetchClassesInMySchool(), fetchMyEnrollRequests()]);
      const enriched = await Promise.all(
        cls.map(async (c) => {
          const canViewStats = !!c.is_member || !!c.is_admin;
          if (!canViewStats) {
            return { ...c, ratingPercent: null, ratingDelta: null, ratingTrend: null, ratingTrendWindowDays: null };
          }
          try {
            const st = await fetchClassroomStats(c.id);
            return {
              ...c,
              ratingPercent: st?.rating_percent ?? null,
              ratingDelta: st?.rating_delta ?? null,
              ratingTrend: st?.rating_trend ?? null,
              ratingTrendWindowDays: st?.rating_trend_window_days ?? null,
            };
          } catch {
            return { ...c, ratingPercent: null, ratingDelta: null, ratingTrend: null, ratingTrendWindowDays: null };
          }
        })
      );
      setClasses(enriched);
      setEnrollRequests(req);
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const loadStudents = async (classId: number) => {
    setStudentsLoading(true);
    setStudentsError(null);
    try {
      const data = await fetchStudentsInClassroom(classId);
      setStudents(data);
    } catch (err: any) {
      setStudentsError(parseApiError(err));
    } finally {
      setStudentsLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!me) return;
    void loadBaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, me?.id]);

  useEffect(() => {
    if (!selectedClassId) return;
    void loadStudents(selectedClassId);
  }, [selectedClassId]);

  const handleCreate = async (name: string) => {
    if (isB2B) {
      setError('Mod B2B: clasele sunt create de secretariat.');
      return;
    }
    setError(null);
    setInfo(null);
    setIsLoading(true);
    try {
      await createClassroom(name);
      setInfo('Clasa a fost creată cu succes.');
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      setSearchParams(next, { replace: true });
      await loadBaseData();
    } catch (err: any) {
      setError(parseApiError(err));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoin = async (classId: number) => {
    setError(null);
    setInfo(null);
    setIsLoading(true);
    try {
      await joinClassroom(classId);
      setInfo('Cererea de aderare a fost trimisă.');
      await loadBaseData();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeave = async (classId: number) => {
    if (!window.confirm('Ești sigur că vrei să părăsești clasa?')) return;
    setError(null);
    setInfo(null);
    setIsLoading(true);
    try {
      const resp = await leaveClassroom(classId);
      setInfo(resp.message || 'Ai părăsit clasa.');
      if (selectedClassId === classId) {
        const next = new URLSearchParams(searchParams);
        next.delete('classId');
        next.delete('tab');
        setSearchParams(next);
      }
      await loadBaseData();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const openClass = (classId: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('classId', String(classId));
    next.set('tab', 'catalog');
    next.delete('action');
    setSearchParams(next);
  };

  const closeClass = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('classId');
    next.delete('tab');
    setSearchParams(next);
  };

  const setTab = (tab: 'catalog' | 'teme' | 'cursuri' | 'students' | 'orar' | 'profesori') => {
    if (!selectedClassId) return;
    const next = new URLSearchParams(searchParams);
    next.set('classId', String(selectedClassId));
    next.set('tab', tab);
    setSearchParams(next);
  };

  const openRename = (classId: number, currentName: string) => {
    if (isB2B) {
      setError('Mod B2B: clasele sunt create de secretariat.');
      setInfo(null);
      return;
    }
    setRenameClassId(classId);
    setRenameName(currentName);
    setShowRenameModal(true);
  };

  const submitRename = async () => {
    if (!renameClassId) return;
    const newName = renameName.trim();
    if (!newName) return;
    if (!window.confirm('Ești sigur că vrei să modifici numele clasei?')) return;
    setError(null);
    setInfo(null);
    setIsLoading(true);
    try {
      await renameClassroom(renameClassId, newName);
      setShowRenameModal(false);
      setRenameClassId(null);
      setRenameName('');
      setInfo('Numele clasei a fost modificat.');
      await loadBaseData();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const openDelete = (classId: number) => {
    if (isB2B) {
      setError('Mod B2B: clasele sunt create de secretariat.');
      setInfo(null);
      return;
    }
    setError(null);
    setInfo(null);
    setDeleteClassId(classId);
    setDeletePassword('');
    setShowDeleteModal(true);
  };

  const submitDelete = async () => {
    if (!deleteClassId) return;
    if (!deletePassword.trim()) {
      setError('Parola este obligatorie.');
      return;
    }
    if (!window.confirm('Ești sigur că vrei să ștergi clasa?')) return;
    setError(null);
    setInfo(null);
    setIsLoading(true);
    try {
      await deleteClassroom(deleteClassId, deletePassword);
      setShowDeleteModal(false);
      setDeleteClassId(null);
      setDeletePassword('');
      setInfo('Clasa a fost ștearsă.');
      if (selectedClassId === deleteClassId) closeClass();
      await loadBaseData();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const openAddAdmin = async (classId: number) => {
    setAddAdminClassId(classId);
    setTeachers([]);
    setSelectedTeacherId(null);
    setShowAddAdminModal(true);
    try {
      const list = await fetchTeachersInClassroom(classId);
      setTeachers(list);
    } catch (err: any) {
      setError(parseApiError(err));
    }
  };

  const submitAddAdmin = async () => {
    if (!addAdminClassId || !selectedTeacherId) return;
    setError(null);
    setInfo(null);
    setIsLoading(true);
    try {
      await addAdminToClassroom(addAdminClassId, selectedTeacherId);
      setShowAddAdminModal(false);
      setAddAdminClassId(null);
      setSelectedTeacherId(null);
      setInfo('Profesorul a fost promovat ca admin.');
      await loadBaseData();
    } catch (err: any) {
      setError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStudent = async (payload: StudentCreatePayload) => {
    if (!selectedClassId) return;
    if (isB2B) {
      setStudentsError('Mod B2B: elevii sunt gestionați de secretariat.');
      return;
    }
    setStudentsError(null);
    setInfo(null);
    setIsLoading(true);
    try {
      await addStudentToClassroom(selectedClassId, payload);
      setInfo('Elevul a fost adăugat. Invitația pe email a fost trimisă automat.');
      await loadStudents(selectedClassId);
    } catch (err: any) {
      setStudentsError(parseApiError(err));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteStudent = async (studentId: number) => {
    if (!selectedClassId) return;
    if (!window.confirm('Ștergi elevul din clasă (și contul lui)?')) return;
    setStudentsError(null);
    setInfo(null);
    setIsLoading(true);
    try {
      await deleteStudentFromClassroom(selectedClassId, studentId);
      setInfo('Elevul a fost șters.');
      await loadStudents(selectedClassId);
    } catch (err: any) {
      setStudentsError(parseApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) return <div className="min-h-screen bg-background" />;

  if (!me) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full">
          <p className="text-sm text-muted-foreground mb-4">Te rugăm să te autentifici.</p>
          <Button variant="default" onClick={() => navigate('/authentication-portal')}>Autentificare</Button>
        </div>
      </div>
    );
  }

  if (uiRole !== 'teacher') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full">
          <p className="text-sm text-muted-foreground">Această pagină este disponibilă doar profesorilor.</p>
        </div>
      </div>
    );
  }

  const requestCreateOpen = actionParam === 'create';
  const consumeCreateOpen = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        notificationCount={0}
        onNavigate={(path) => navigate(path)}
        onSignOut={() => {
          signOut();
          navigate('/authentication-portal');
        }}
      />
      <DashboardSidebar user={user} isCollapsed={isSidebarCollapsed} onNavigate={(path) => navigate(path)} />

      <main className={`pt-16 transition-all duration-300 ${isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {!selectedClassId || !selectedClass ? (
          <ClassroomsListView
            classes={classes}
            enrollRequests={enrollRequests}
            isLoading={isLoading}
            error={error}
            info={info}
            isB2B={isB2B}
            onCreateClassroom={handleCreate}
            onJoin={handleJoin}
            onLeave={handleLeave}
            onOpenClass={openClass}
            onRename={openRename}
            onDelete={openDelete}
            onAddAdmin={openAddAdmin}
            requestCreateOpen={requestCreateOpen}
            onConsumeCreateOpen={consumeCreateOpen}
          />
        ) : (
          <ClassroomDetailView
            classId={selectedClass.id}
            className={selectedClass.name}
            profesor={selectedClass.profesor}
            isAdmin={selectedClass.is_admin}
            canEditStudents={uiRole === 'admin' || (!isB2B && uiRole === 'teacher')}
            isB2B={isB2B}
            activeTab={activeTab}
            onTabChange={setTab}
            students={students}
            studentsLoading={studentsLoading}
            studentsError={studentsError}
            info={info}
            onBack={closeClass}
            onRefreshStudents={() => loadStudents(selectedClass.id)}
            onDeleteStudent={handleDeleteStudent}
            onAddStudent={handleAddStudent}
            onRename={() => openRename(selectedClass.id, selectedClass.name)}
            onDeleteClass={() => openDelete(selectedClass.id)}
            onAddAdmin={() => openAddAdmin(selectedClass.id)}
          />
        )}
      </main>

      {showRenameModal && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md animate-scale-in">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Redenumește clasa</h3>
              <button className="p-2 rounded-md hover:bg-muted" onClick={() => setShowRenameModal(false)} aria-label="Închide">
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <input
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                placeholder="Noul nume al clasei"
              />
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowRenameModal(false)}>Anulează</Button>
                <Button variant="default" onClick={submitRename} disabled={isLoading || !renameName.trim()}>
                  Salvează
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md animate-scale-in">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Șterge clasa</h3>
              <button className="p-2 rounded-md hover:bg-muted" onClick={() => setShowDeleteModal(false)} aria-label="Închide">
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Pentru siguranță, confirmă parola contului tău. Doar un admin poate șterge clasa.
              </p>
              {error && (
                <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>
              )}
              <Input
                label="Parolă"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Parola ta"
              />
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Anulează</Button>
                <Button variant="destructive" onClick={submitDelete} disabled={isLoading || !deletePassword.trim()}>
                  Șterge
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddAdminModal && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md animate-scale-in">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Promovează profesor ca admin</h3>
              <button className="p-2 rounded-md hover:bg-muted" onClick={() => setShowAddAdminModal(false)} aria-label="Închide">
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <label className="block text-sm font-medium text-foreground">
                Selectează profesor
                <select
                  className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  value={selectedTeacherId || ''}
                  onChange={(e) => setSelectedTeacherId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">-- selectează profesor --</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id} disabled={t.is_admin}>
                      {t.full_name} {t.is_admin ? '(admin)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAddAdminModal(false)}>Anulează</Button>
                <Button variant="default" onClick={submitAddAdmin} disabled={isLoading || !selectedTeacherId}>
                  Promovează
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassManagementHub;
